import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export type Currency = 'PYG' | 'USD';

export interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  exchangeRate: number; // Rate from PYG to this currency
}

export const useCurrencyStore = defineStore('currency', () => {
  const currentCurrency = ref<Currency>('PYG');
  const exchangeRates = ref({
    PYG: 1,
    USD: 0.000135, // 1 PYG = 0.000135 USD (approximately 7400 PYG = 1 USD)
  });

  const currencies: Record<Currency, CurrencyConfig> = {
    PYG: {
      code: 'PYG',
      symbol: 'Gs.',
      name: 'Guaraníes',
      exchangeRate: 1,
    },
    USD: {
      code: 'USD',
      symbol: '$',
      name: 'Dólares',
      exchangeRate: 0.000135,
    },
  };

  const currentCurrencyConfig = computed(() => currencies[currentCurrency.value]);

  const setCurrency = (currency: Currency) => {
    currentCurrency.value = currency;
    localStorage.setItem('selected_currency', currency);
    
    // Track currency change
    if (window.trackCustomEvent) {
      window.trackCustomEvent('Currency', 'Changed', {
        fromCurrency: currentCurrency.value,
        toCurrency: currency
      });
    }
  };

  const convertPrice = (priceInPYG: number, targetCurrency?: Currency): number => {
    const target = targetCurrency || currentCurrency.value;
    return priceInPYG * exchangeRates.value[target];
  };

  const formatPrice = (priceInSelectedCurrency: number, targetCurrency?: Currency): string => {
    const target = targetCurrency || currentCurrency.value;
    const config = currencies[target];
    
    // Assume the price is already in the selected currency
    // This is used when the cart store has already converted prices
    if (target === 'USD') {
      return `${config.symbol}${priceInSelectedCurrency.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else {
      return `${config.symbol} ${Math.round(priceInSelectedCurrency).toLocaleString('es-PY', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    }
  };

  const updateExchangeRates = async () => {
    try {
      // In a real implementation, fetch from an exchange rate API
      // For now, we'll use static rates
      // const response = await fetch('https://api.exchangerate-api.com/v4/latest/PYG');
      // const data = await response.json();
      // exchangeRates.value.USD = data.rates.USD;
    } catch (error) {
      console.error('Error updating exchange rates:', error);
    }
  };

  const loadSavedCurrency = () => {
    const saved = localStorage.getItem('selected_currency') as Currency;
    if (saved && currencies[saved]) {
      currentCurrency.value = saved;
    }
  };

  return {
    currentCurrency: computed(() => currentCurrency.value),
    currentCurrencyConfig,
    currencies,
    exchangeRates: computed(() => exchangeRates.value),
    setCurrency,
    convertPrice,
    formatPrice,
    updateExchangeRates,
    loadSavedCurrency,
  };
});
